import memoize, { memoizeClear } from "@-/com.npmjs.memoize";
import { makeMutable, type SharedValue } from "@-/com.npmjs.react-native-reanimated";
import type { SkImage } from "@-/com.npmjs.react-native-skia";

/**
 * Where the welcome page keeps its galaxy canvas's particles and textures, filled by the canvas
 * (which alone knows what they are). Every canvas of the scene shares them, so the entry host's
 * never rebuilds them as the entry begins.
 */
export type WelcomeGalaxyCanvasCache = { current: object | null };

/**
 * What the welcome galaxy launches out of before its flight: the native splash on a cold launch,
 * or a picture of the app's last screen on sign-out (`captureWelcomeGalaxyExit`).
 */
export type WelcomeGalaxySceneLaunch =
  | { kind: "snapshot"; snapshot: SkImage }
  | { kind: "splash" }
  | null;

/**
 * The live welcome galaxy, as the welcome page publishes it: the shared values its canvas draws
 * from. The entry host draws a second canvas from the very same values, so the moment it takes
 * over is invisible.
 */
export type WelcomeGalaxyScene = {
  /** Which galaxy the page draws: light on deep space, or its ink negative on white. */
  appearance: "dark" | "light";
  canvasCache: WelcomeGalaxyCanvasCache;
  /** Seconds since the entry into the Haniverse began; 0 until it has. */
  entryTime: SharedValue<number>;
  /** What the scene opened on and launched out of (and still draws), if anything. */
  launch: WelcomeGalaxySceneLaunch;
  simTime: SharedValue<number>;
  spinOffset: SharedValue<number>;
  /** `stageWidth`: where the galaxy is centred (see `WelcomeGalaxyViewport`). */
  viewport: { height: number; stageWidth?: number; width: number };
};

/**
 * The Learn card the entry's galaxy morphs into, in window coordinates (dp), with its corner
 * radius. Published by whichever card is on the Learn summary (the loaded one or its first-paint
 * fallback), so the galaxy squares off exactly onto the card that then shows through it.
 */
export type WelcomeGalaxyEntryCard = {
  height: number;
  radius: number;
  width: number;
  x: number;
  y: number;
};

/**
 * What the entry into the Haniverse turns the galaxy into: the Learn card (a new account that
 * skips the onboarding), or nothing at all: the camera dives on into the galaxy's core, out of
 * which comes the onboarding's Earth (`globe`, a new learner) or Learn's map, the app's home
 * (`map`, signing in).
 */
export type WelcomeGalaxyEntryDestination = "card" | "globe" | "map";

/** Whether `destination` is a dive into the galaxy's core, which becomes no card. */
export function isWelcomeGalaxyDive(destination: WelcomeGalaxyEntryDestination): boolean {
  "worklet";
  return destination !== "card";
}

/**
 * Plays the entry into the Haniverse over `scene`, calling `navigate` exactly once (while the
 * galaxy covers the screen). Returns `false` if it cannot play it, in which case the caller
 * navigates straight away.
 */
export type WelcomeGalaxyEntryHost = (request: {
  navigate: () => void;
  scene: WelcomeGalaxyScene;
}) => boolean;

/**
 * Takes a picture of the app as it is on screen, for the welcome page to launch out of once the
 * user has signed out. Resolves to `null` when there is nothing to capture (and never rejects).
 */
export type WelcomeGalaxyExitCapturer = () => Promise<SkImage | null>;

interface WelcomeGalaxyEntryState {
  /**
   * The entry time (s) at which the dive under way starts to dissolve into the Earth beneath it,
   * as the entry host decides it (`welcomeGalaxyDiveRevealStart`); `null` until then.
   */
  diveRevealStart: SharedValue<number | null>;
  /** Whether the dive's destination (the Earth, or the map) is on screen beneath it. */
  diveTargetShown: SharedValue<boolean>;
  /** Where the entry's galaxy is to square off to; `null` while no card has reported. */
  entryCard: SharedValue<WelcomeGalaxyEntryCard | null>;
  /** What the entry under way (or the next one) turns the galaxy into. */
  entryDestination: SharedValue<WelcomeGalaxyEntryDestination>;
  exitCapturer: WelcomeGalaxyExitCapturer | null;
  /** The picture of the app a sign-out captured, until the welcome page takes it. */
  exitSnapshot: SkImage | null;
  host: WelcomeGalaxyEntryHost | null;
  scene: WelcomeGalaxyScene | null;
}

// Memoized rather than a top-level `let`, so tests can reset it with `memoizeClear`.
const getWelcomeGalaxyEntryState = memoize((): WelcomeGalaxyEntryState => ({
  diveRevealStart: makeMutable<number | null>(null),
  diveTargetShown: makeMutable(false),
  entryCard: makeMutable<WelcomeGalaxyEntryCard | null>(null),
  entryDestination: makeMutable<WelcomeGalaxyEntryDestination>("card"),
  exitCapturer: null,
  exitSnapshot: null,
  host: null,
  scene: null,
}));

/** Registers the (one) entry host, mounted above the routes. Returns the unregister function. */
export function registerWelcomeGalaxyEntryHost(host: WelcomeGalaxyEntryHost): () => void {
  const state = getWelcomeGalaxyEntryState();
  state.host = host;
  return () => {
    if (state.host === host) {
      state.host = null;
    }
  };
}

/** Publishes the mounted welcome galaxy. Returns the unregister function. */
export function registerWelcomeGalaxyScene(scene: WelcomeGalaxyScene): () => void {
  const state = getWelcomeGalaxyEntryState();
  state.scene = scene;
  return () => {
    if (state.scene === scene) {
      state.scene = null;
    }
  };
}

/**
 * Leaves the welcome galaxy for the app by entering it: the galaxy zooms in and becomes the Learn
 * card while the app fades in around it, or (`destination: "globe"` or `"map"`) dives on into its
 * core while the onboarding's Earth, or Learn's map, comes out of it. Without a mounted galaxy or host (web, reduced motion, a
 * build without Skia) it just navigates.
 */
export function enterHaniverseFromWelcome(
  navigate: () => void,
  destination: WelcomeGalaxyEntryDestination = "card",
): void {
  const { diveRevealStart, entryDestination, host, scene } = getWelcomeGalaxyEntryState();
  if (scene !== null && scene.entryTime.get() === 0) {
    entryDestination.set(destination);
    diveRevealStart.set(null);
  }
  if (host !== null && scene !== null && scene.entryTime.get() === 0 && host({ navigate, scene })) {
    return;
  }
  navigate();
}

/**
 * When the dive under way starts to dissolve (see `WelcomeGalaxyEntryState.diveRevealStart`), as a
 * shared value the entry host writes and the galaxy's UI-thread frame reads.
 */
export function welcomeGalaxyDiveRevealStart(): SharedValue<number | null> {
  return getWelcomeGalaxyEntryState().diveRevealStart;
}

/**
 * Whether the dive's destination (the onboarding's Earth, or Learn's map) is on screen, beneath
 * the dive into the galaxy's core that leads to it, as a shared value the entry host reads every
 * frame.
 */
export function welcomeGalaxyDiveTarget(): SharedValue<boolean> {
  return getWelcomeGalaxyEntryState().diveTargetShown;
}

/**
 * The dive's destination (the onboarding's Earth, or Learn's map home) reports that it is on
 * screen (`true`), so a dive into the galaxy's core can dissolve into it, or that it has gone
 * (`false`).
 */
export function publishWelcomeGalaxyDiveTarget(isShown: boolean): void {
  getWelcomeGalaxyEntryState().diveTargetShown.set(isShown);
}

/** What the entry turns the galaxy into, as a shared value the galaxy's UI-thread frame reads. */
export function welcomeGalaxyEntryDestination(): SharedValue<WelcomeGalaxyEntryDestination> {
  return getWelcomeGalaxyEntryState().entryDestination;
}

/**
 * The card the entry's galaxy squares off onto, as a shared value the galaxy's UI-thread frame
 * reads every frame. `null` until a Learn card has reported its frame (the entry then aims at
 * where the card is expected).
 */
export function welcomeGalaxyEntryCard(): SharedValue<WelcomeGalaxyEntryCard | null> {
  return getWelcomeGalaxyEntryState().entryCard;
}

/** The Learn card on screen reports where it is; `null` once it has gone. */
export function publishWelcomeGalaxyEntryCard(card: WelcomeGalaxyEntryCard | null): void {
  getWelcomeGalaxyEntryState().entryCard.set(card);
}

/** Registers the (one) view that can picture the app for a sign-out. Returns the unregister function. */
export function registerWelcomeGalaxyExitCapturer(capturer: WelcomeGalaxyExitCapturer): () => void {
  const state = getWelcomeGalaxyEntryState();
  state.exitCapturer = capturer;
  return () => {
    if (state.exitCapturer === capturer) {
      state.exitCapturer = null;
    }
  };
}

/**
 * Pictures the app as it is now, for the welcome page that sign-out leads to, so it can launch
 * out of this very screen instead of cutting to the galaxy. Call it before the sign-out erases
 * the account (and with it the app). Without a capturer (web, a build without Skia) it does
 * nothing, and the welcome page simply starts on its flight.
 */
export async function captureWelcomeGalaxyExit(): Promise<void> {
  const state = getWelcomeGalaxyEntryState();
  discardWelcomeGalaxyExit();
  if (state.exitCapturer === null) {
    return;
  }
  state.exitSnapshot = await state.exitCapturer();
}

/** Forgets a captured picture of the app, should the sign-out it was taken for not go through. */
export function discardWelcomeGalaxyExit(): void {
  const state = getWelcomeGalaxyEntryState();
  state.exitSnapshot?.dispose();
  state.exitSnapshot = null;
}

/**
 * The picture of the app a sign-out captured, for the welcome page mounting after it: taken once,
 * so only that page launches out of it. The page owns the image from here on.
 */
export function takeWelcomeGalaxyExitSnapshot(): SkImage | null {
  const state = getWelcomeGalaxyEntryState();
  const { exitSnapshot } = state;
  state.exitSnapshot = null;
  return exitSnapshot;
}

/** Test-only: forget the registered host, scene, card, capturer and snapshot. */
export function resetWelcomeGalaxyEntryForTests(): void {
  memoizeClear(getWelcomeGalaxyEntryState);
}
